import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import api from '../services/api';
import configuratorService from '../services/configuratorService';
import PdfPreviewModal from '../components/PdfPreviewModal';

// ==========================================
// Styled Components
// ==========================================

const PageContainer = styled.div`
  min-height: 100vh;
  background: #f3f4f6;
`;

const Header = styled.div`
  background: white;
  border-bottom: 1px solid #e5e7eb;
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const HeaderTitle = styled.div`
  h1 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 0.25rem 0;
  }
  p {
    color: #6b7280;
    font-size: 0.875rem;
    margin: 0;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
`;

const ViewToggle = styled.div`
  display: flex;
  background: #f3f4f6;
  border-radius: 8px;
  padding: 4px;
`;

const ViewButton = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.active ? 'white' : 'transparent'};
  color: ${props => props.active ? '#3b82f6' : '#6b7280'};
  box-shadow: ${props => props.active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'};
  
  &:hover {
    color: #3b82f6;
  }
`;

const FilterBar = styled.div`
  background: white;
  border-bottom: 1px solid #e5e7eb;
  padding: 0.75rem 1.5rem;
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const FilterSelect = styled.select`
  padding: 0.5rem 2rem 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const SearchInput = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.875rem;
  width: 200px;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Checkbox = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #6b7280;
  cursor: pointer;
  
  input {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
`;

// Kanban Styles
const KanbanContainer = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  overflow-x: auto;
  min-height: calc(100vh - 180px);
`;

const KanbanColumn = styled.div`
  flex: 0 0 300px;
  background: #f9fafb;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 200px);
`;

const ColumnHeader = styled.div`
  padding: 1rem;
  border-bottom: 3px solid ${props => props.color || '#6b7280'};
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  border-radius: 12px 12px 0 0;
`;

const ColumnTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  .color-dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${props => props.color || '#6b7280'};
  }
  
  h3 {
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
    margin: 0;
  }
`;

const ColumnCount = styled.span`
  background: ${props => props.color || '#6b7280'}20;
  color: ${props => props.color || '#6b7280'};
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
`;

const ColumnTotal = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  padding: 0.5rem 1rem;
  text-align: center;
  background: white;
  border-top: 1px solid #e5e7eb;
`;

const CardsContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const OrderCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  cursor: pointer;
  transition: all 0.2s;
  border-left: 4px solid ${props => props.borderColor || '#6b7280'};
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    transform: translateY(-2px);
  }
  
  &.dragging {
    opacity: 0.5;
    transform: rotate(3deg);
  }
  
  &.drag-over {
    border: 2px dashed #3b82f6;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
`;

const CardOrderNumber = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: #3b82f6;
`;

const CardAmount = styled.span`
  font-size: 0.875rem;
  font-weight: 700;
  color: #059669;
`;

const CardCustomer = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.25rem;
`;

const CardCompany = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 0.5rem;
`;

const CardMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.75rem;
  color: #9ca3af;
`;

const CardBadge = styled.span`
  font-size: 0.625rem;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  font-weight: 500;
  background: ${props => props.bg || '#f3f4f6'};
  color: ${props => props.color || '#6b7280'};
`;

const CardActions = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid #f3f4f6;
`;

const CardActionBtn = styled.button`
  flex: 1;
  padding: 0.375rem;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: white;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.15s;
  
  &:hover {
    background: #f3f4f6;
    border-color: #d1d5db;
  }
`;

// List View Styles
const ListContainer = styled.div`
  padding: 1.5rem;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1rem;
  border-left: 4px solid ${props => props.color || '#6b7280'};
  
  h4 {
    font-size: 0.75rem;
    color: #6b7280;
    margin: 0 0 0.25rem 0;
    text-transform: uppercase;
  }
  
  .value {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1f2937;
  }
  
  .amount {
    font-size: 0.875rem;
    color: #059669;
    margin-top: 0.25rem;
  }
`;

const TableCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  th, td {
    padding: 0.75rem 1rem;
    text-align: left;
    border-bottom: 1px solid #f3f4f6;
  }
  
  th {
    background: #f9fafb;
    font-size: 0.75rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
  }
  
  td {
    font-size: 0.875rem;
    color: #374151;
  }
  
  tr:hover td {
    background: #f9fafb;
  }
`;

const PipelineStatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.625rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${props => props.color}20;
  color: ${props => props.color};
  
  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${props => props.color};
  }
`;

const ActionButton = styled.button`
  padding: 0.375rem 0.625rem;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: white;
  font-size: 0.75rem;
  cursor: pointer;
  margin-right: 0.25rem;
  
  &:hover {
    background: #f3f4f6;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #6b7280;
  
  h3 {
    font-size: 1.125rem;
    margin-bottom: 0.5rem;
  }
  
  p {
    font-size: 0.875rem;
  }
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  
  h2 {
    margin: 0;
    font-size: 1.25rem;
    color: #1f2937;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
  
  label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 0.375rem;
  }
  
  input, select, textarea {
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    font-size: 0.875rem;
    
    &:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
  }
  
  textarea {
    resize: vertical;
    min-height: 80px;
  }
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  
  &.primary {
    background: #3b82f6;
    color: white;
    border: none;
    
    &:hover {
      background: #2563eb;
    }
  }
  
  &.secondary {
    background: white;
    color: #374151;
    border: 1px solid #e5e7eb;
    
    &:hover {
      background: #f9fafb;
    }
  }
  
  &.danger {
    background: #ef4444;
    color: white;
    border: none;
    
    &:hover {
      background: #dc2626;
    }
  }
`;

// ==========================================
// Main Component
// ==========================================

function OrdersPipeline() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [orders, setOrders] = useState([]);
  const [pipelineStatuses, setPipelineStatuses] = useState([]);
  const [configurators, setConfigurators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // View toggle: 'kanban' or 'list'
  const [viewMode, setViewMode] = useState(searchParams.get('view') || 'kanban');
  
  // Filters
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    order_type: searchParams.get('type') || '',
    configurator_id: searchParams.get('configurator') || '',
    show_archived: searchParams.get('archived') === 'true'
  });
  
  // Selected order for modal
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  
  // PDF Preview modal
  const [pdfPreviewOrder, setPdfPreviewOrder] = useState(null);
  
  // Drag state
  const [draggedOrder, setDraggedOrder] = useState(null);
  const [dragOverStatus, setDragOverStatus] = useState(null);

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [ordersRes, statusesRes, configuratorsRes] = await Promise.all([
        api.get('/orders'),
        configuratorService.getPipelineStatuses(),
        configuratorService.getConfigurators(true)
      ]);
      
      setOrders(ordersRes.data.orders || []);
      setPipelineStatuses(statusesRes.statuses || []);
      setConfigurators(configuratorsRes.configurators || []);
      setError('');
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (viewMode !== 'kanban') params.set('view', viewMode);
    if (filters.search) params.set('search', filters.search);
    if (filters.order_type) params.set('type', filters.order_type);
    if (filters.configurator_id) params.set('configurator', filters.configurator_id);
    if (filters.show_archived) params.set('archived', 'true');
    setSearchParams(params);
  }, [viewMode, filters, setSearchParams]);

  // Filter orders
  const filteredOrders = orders.filter(order => {
    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const matchesSearch = 
        order.order_number?.toLowerCase().includes(searchLower) ||
        order.customer_name?.toLowerCase().includes(searchLower) ||
        order.customer_email?.toLowerCase().includes(searchLower) ||
        order.company_name?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }
    
    // Order type filter
    if (filters.order_type && order.order_type !== filters.order_type) {
      return false;
    }
    
    // Configurator filter
    if (filters.configurator_id && order.configurator_id !== parseInt(filters.configurator_id)) {
      return false;
    }
    
    // Archived filter
    if (!filters.show_archived && order.is_archived) {
      return false;
    }
    
    return true;
  });

  // Group orders by pipeline status for Kanban view
  const ordersByStatus = pipelineStatuses.reduce((acc, status) => {
    acc[status.id] = filteredOrders.filter(o => 
      o.pipeline_status?.id === status.id
    );
    return acc;
  }, {});

  // Also include orders without a pipeline status
  const unassignedOrders = filteredOrders.filter(o => !o.pipeline_status);

  // Calculate pipeline stats
  const pipelineStats = pipelineStatuses.map(status => ({
    ...status,
    count: ordersByStatus[status.id]?.length || 0,
    total: ordersByStatus[status.id]?.reduce((sum, o) => sum + (o.total_amount || 0), 0) || 0
  }));

  // Drag handlers
  const handleDragStart = (e, order) => {
    setDraggedOrder(order);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, statusId) => {
    e.preventDefault();
    setDragOverStatus(statusId);
  };

  const handleDragLeave = () => {
    setDragOverStatus(null);
  };

  const handleDrop = async (e, statusId) => {
    e.preventDefault();
    setDragOverStatus(null);
    
    if (!draggedOrder || draggedOrder.pipeline_status?.id === statusId) {
      setDraggedOrder(null);
      return;
    }
    
    try {
      await configuratorService.updateOrderPipelineStatus(draggedOrder.id, statusId);
      
      // Update local state
      setOrders(prev => prev.map(o => 
        o.id === draggedOrder.id 
          ? { ...o, pipeline_status: pipelineStatuses.find(s => s.id === statusId) }
          : o
      ));
    } catch (err) {
      console.error('Failed to update order status:', err);
      alert('Failed to update order status');
    }
    
    setDraggedOrder(null);
  };

  // Order actions
  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
  };

  const handleDuplicateOrder = async (orderId) => {
    try {
      const result = await configuratorService.duplicateOrder(orderId);
      await loadData();
      alert(`Order duplicated: ${result.order_number}`);
    } catch (err) {
      console.error('Failed to duplicate order:', err);
      alert('Failed to duplicate order');
    }
  };

  const handleArchiveOrder = async (orderId, isArchived) => {
    try {
      if (isArchived) {
        await configuratorService.unarchiveOrder(orderId);
      } else {
        await configuratorService.archiveOrder(orderId);
      }
      await loadData();
    } catch (err) {
      console.error('Failed to archive/unarchive order:', err);
      alert('Failed to update order');
    }
  };

  const handleDownloadPdf = async (orderId) => {
    try {
      await configuratorService.downloadOrderPdf(orderId, 'auto', 'en');
    } catch (err) {
      console.error('Failed to download PDF:', err);
      alert('Failed to download PDF');
    }
  };

  const handlePreviewPdf = (order) => {
    setPdfPreviewOrder(order);
  };

  const handleGenerateContract = async (orderId) => {
    try {
      const result = await configuratorService.generateOrderContract(orderId);
      alert(`Contract generated: ${result.contract.contract_number}`);
      
      // Offer to download
      if (result.contract && result.contract.id) {
        if (window.confirm('Contract generated! Download now?')) {
          await configuratorService.downloadContract(result.contract.id);
        }
      }
    } catch (err) {
      console.error('Failed to generate contract:', err);
      alert(err.response?.data?.error || 'Failed to generate contract');
    }
  };

  const handleSendEmail = async (orderId, emailType = 'confirmation', recipient = 'customer') => {
    try {
      const confirmMessage = recipient === 'customer' 
        ? 'Send confirmation email to customer?' 
        : 'Send notification to admin(s)?';
      
      if (!window.confirm(confirmMessage)) {
        return;
      }
      
      await configuratorService.sendOrderEmail(orderId, {
        type: emailType,
        recipient: recipient
      });
      alert('Email sent successfully!');
    } catch (err) {
      console.error('Failed to send email:', err);
      alert(err.response?.data?.error || 'Failed to send email');
    }
  };

  const handleStatusChange = async (orderId, newStatusId) => {
    try {
      await configuratorService.updateOrderPipelineStatus(orderId, newStatusId);
      await loadData();
      setShowOrderModal(false);
    } catch (err) {
      console.error('Failed to update status:', err);
      alert('Failed to update status');
    }
  };

  // Render order card for Kanban
  const renderOrderCard = (order, statusColor) => (
    <OrderCard
      key={order.id}
      borderColor={statusColor}
      draggable
      onDragStart={(e) => handleDragStart(e, order)}
      onClick={() => handleViewOrder(order)}
      className={draggedOrder?.id === order.id ? 'dragging' : ''}
    >
      <CardHeader>
        <CardOrderNumber>{order.order_number}</CardOrderNumber>
        <CardAmount>{order.currency} {parseFloat(order.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</CardAmount>
      </CardHeader>
      
      <CardCustomer>{order.customer_name || order.contact_name || 'Unknown'}</CardCustomer>
      {order.company_name && <CardCompany>{order.company_name}</CardCompany>}
      
      <CardMeta>
        <span>{new Date(order.created_at).toLocaleDateString()}</span>
        {order.order_type === 'configurator' && (
          <CardBadge bg="#dbeafe" color="#2563eb">Quote</CardBadge>
        )}
        {order.is_archived && (
          <CardBadge bg="#fef3c7" color="#d97706">Archived</CardBadge>
        )}
      </CardMeta>
      
      <CardActions onClick={(e) => e.stopPropagation()}>
        <CardActionBtn onClick={() => handlePreviewPdf(order)} title="Preview PDF">👁️</CardActionBtn>
        <CardActionBtn onClick={() => handleDownloadPdf(order.id)} title="Download PDF">📄</CardActionBtn>
        <CardActionBtn onClick={() => handleGenerateContract(order.id)} title="Generate Contract">📝</CardActionBtn>
        <CardActionBtn onClick={() => handleSendEmail(order.id, 'confirmation', 'customer')} title="Send Email">✉️</CardActionBtn>
        <CardActionBtn onClick={() => handleDuplicateOrder(order.id)} title="Duplicate">📋</CardActionBtn>
        <CardActionBtn onClick={() => navigate(`/orders/${order.id}/edit`)} title="Edit">✏️</CardActionBtn>
        <CardActionBtn onClick={() => handleArchiveOrder(order.id, order.is_archived)} title={order.is_archived ? 'Unarchive' : 'Archive'}>
          {order.is_archived ? '📂' : '📦'}
        </CardActionBtn>
      </CardActions>
    </OrderCard>
  );

  if (loading) {
    return (
      <PageContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div className="spinner"></div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Header */}
      <Header>
        <HeaderTitle>
          <h1>📦 Orders Pipeline</h1>
          <p>Manage orders and track through your sales pipeline</p>
        </HeaderTitle>
        
        <HeaderActions>
          <ViewToggle>
            <ViewButton active={viewMode === 'kanban'} onClick={() => setViewMode('kanban')}>
              📊 Kanban
            </ViewButton>
            <ViewButton active={viewMode === 'list'} onClick={() => setViewMode('list')}>
              📋 List
            </ViewButton>
          </ViewToggle>
          
          <Button className="primary" onClick={() => navigate('/orders/new')}>
            ➕ New Order
          </Button>
        </HeaderActions>
      </Header>
      
      {/* Filter Bar */}
      <FilterBar>
        <SearchInput
          type="text"
          placeholder="Search orders..."
          value={filters.search}
          onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
        />
        
        <FilterSelect
          value={filters.order_type}
          onChange={(e) => setFilters(f => ({ ...f, order_type: e.target.value }))}
        >
          <option value="">All Types</option>
          <option value="standard">Standard Orders</option>
          <option value="configurator">Configurator Quotes</option>
        </FilterSelect>
        
        {configurators.length > 0 && (
          <FilterSelect
            value={filters.configurator_id}
            onChange={(e) => setFilters(f => ({ ...f, configurator_id: e.target.value }))}
          >
            <option value="">All Configurators</option>
            {configurators.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </FilterSelect>
        )}
        
        <Checkbox>
          <input
            type="checkbox"
            checked={filters.show_archived}
            onChange={(e) => setFilters(f => ({ ...f, show_archived: e.target.checked }))}
          />
          Show Archived
        </Checkbox>
        
        <div style={{ marginLeft: 'auto', color: '#6b7280', fontSize: '0.875rem' }}>
          {filteredOrders.length} orders
        </div>
      </FilterBar>
      
      {error && (
        <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '1rem 1.5rem' }}>
          {error}
        </div>
      )}
      
      {/* Kanban View */}
      {viewMode === 'kanban' && (
        <KanbanContainer>
          {/* Unassigned column */}
          {unassignedOrders.length > 0 && (
            <KanbanColumn>
              <ColumnHeader color="#9ca3af">
                <ColumnTitle color="#9ca3af">
                  <div className="color-dot" />
                  <h3>Unassigned</h3>
                </ColumnTitle>
                <ColumnCount color="#9ca3af">{unassignedOrders.length}</ColumnCount>
              </ColumnHeader>
              <CardsContainer>
                {unassignedOrders.map(order => renderOrderCard(order, '#9ca3af'))}
              </CardsContainer>
            </KanbanColumn>
          )}
          
          {/* Pipeline status columns */}
          {pipelineStatuses.map(status => (
            <KanbanColumn
              key={status.id}
              onDragOver={(e) => handleDragOver(e, status.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, status.id)}
              style={{
                outline: dragOverStatus === status.id ? `2px dashed ${status.color}` : 'none'
              }}
            >
              <ColumnHeader color={status.color}>
                <ColumnTitle color={status.color}>
                  <div className="color-dot" />
                  <h3>{status.name}</h3>
                </ColumnTitle>
                <ColumnCount color={status.color}>{ordersByStatus[status.id]?.length || 0}</ColumnCount>
              </ColumnHeader>
              
              <CardsContainer>
                {(ordersByStatus[status.id] || []).map(order => 
                  renderOrderCard(order, status.color)
                )}
                {(!ordersByStatus[status.id] || ordersByStatus[status.id].length === 0) && (
                  <EmptyState style={{ padding: '1rem' }}>
                    <p style={{ fontSize: '0.75rem' }}>Drop orders here</p>
                  </EmptyState>
                )}
              </CardsContainer>
              
              <ColumnTotal>
                Total: {status.currency || 'EUR'} {(pipelineStats.find(s => s.id === status.id)?.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </ColumnTotal>
            </KanbanColumn>
          ))}
          
          {pipelineStatuses.length === 0 && (
            <EmptyState>
              <h3>No Pipeline Statuses Configured</h3>
              <p>Set up pipeline statuses in Configurators → Pipeline Settings</p>
              <Button className="primary" style={{ marginTop: '1rem' }} onClick={() => navigate('/configurators/pipeline-settings')}>
                Configure Pipeline
              </Button>
            </EmptyState>
          )}
        </KanbanContainer>
      )}
      
      {/* List View */}
      {viewMode === 'list' && (
        <ListContainer>
          {/* Stats Row */}
          <StatsRow>
            {pipelineStats.map(stat => (
              <StatCard key={stat.id} color={stat.color}>
                <h4>{stat.name}</h4>
                <div className="value">{stat.count}</div>
                <div className="amount">
                  {stat.currency || 'EUR'} {stat.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
              </StatCard>
            ))}
          </StatsRow>
          
          {/* Orders Table */}
          <TableCard>
            <Table>
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Customer</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Amount</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      <EmptyState>
                        <h3>No orders found</h3>
                        <p>Try adjusting your filters or create a new order</p>
                      </EmptyState>
                    </td>
                  </tr>
                ) : (
                  filteredOrders.map(order => (
                    <tr key={order.id} style={{ opacity: order.is_archived ? 0.6 : 1 }}>
                      <td>
                        <span style={{ color: '#3b82f6', fontWeight: 500 }}>
                          {order.order_number}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{order.customer_name || order.contact_name || '-'}</div>
                        {order.company_name && (
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{order.company_name}</div>
                        )}
                      </td>
                      <td>
                        <CardBadge 
                          bg={order.order_type === 'configurator' ? '#dbeafe' : '#f3f4f6'}
                          color={order.order_type === 'configurator' ? '#2563eb' : '#6b7280'}
                        >
                          {order.order_type === 'configurator' ? 'Quote' : 'Order'}
                        </CardBadge>
                      </td>
                      <td>
                        {order.pipeline_status ? (
                          <PipelineStatusBadge color={order.pipeline_status.color}>
                            <span className="dot" />
                            {order.pipeline_status.name}
                          </PipelineStatusBadge>
                        ) : (
                          <span style={{ color: '#9ca3af' }}>-</span>
                        )}
                      </td>
                      <td style={{ fontWeight: 600, color: '#059669' }}>
                        {order.currency} {parseFloat(order.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td style={{ color: '#6b7280' }}>
                        {new Date(order.created_at).toLocaleDateString()}
                      </td>
                      <td>
                        <ActionButton onClick={() => handlePreviewPdf(order)} title="Preview PDF">👁️</ActionButton>
                        <ActionButton onClick={() => handleDownloadPdf(order.id)} title="Download PDF">📄</ActionButton>
                        <ActionButton onClick={() => handleGenerateContract(order.id)} title="Generate Contract">📝</ActionButton>
                        <ActionButton onClick={() => handleSendEmail(order.id, 'confirmation', 'customer')} title="Send Email">✉️</ActionButton>
                        <ActionButton onClick={() => handleDuplicateOrder(order.id)} title="Duplicate">📋</ActionButton>
                        <ActionButton onClick={() => navigate(`/orders/${order.id}/edit`)} title="Edit">✏️</ActionButton>
                        <ActionButton onClick={() => handleArchiveOrder(order.id, order.is_archived)} title={order.is_archived ? 'Unarchive' : 'Archive'}>
                          {order.is_archived ? '📂' : '📦'}
                        </ActionButton>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </TableCard>
        </ListContainer>
      )}
      
      {/* Order Detail Modal */}
      {showOrderModal && selectedOrder && (
        <ModalOverlay onClick={() => setShowOrderModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h2>{selectedOrder.order_number}</h2>
              <Button className="secondary" onClick={() => setShowOrderModal(false)}>✕</Button>
            </ModalHeader>
            
            <ModalBody>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <FormGroup>
                  <label>Customer</label>
                  <input type="text" value={selectedOrder.customer_name || selectedOrder.contact_name || '-'} disabled />
                </FormGroup>
                <FormGroup>
                  <label>Company</label>
                  <input type="text" value={selectedOrder.company_name || '-'} disabled />
                </FormGroup>
                <FormGroup>
                  <label>Email</label>
                  <input type="text" value={selectedOrder.customer_email || selectedOrder.contact_email || '-'} disabled />
                </FormGroup>
                <FormGroup>
                  <label>Total Amount</label>
                  <input type="text" value={`${selectedOrder.currency} ${parseFloat(selectedOrder.total_amount || 0).toFixed(2)}`} disabled />
                </FormGroup>
              </div>
              
              <FormGroup>
                <label>Pipeline Status</label>
                <select
                  value={selectedOrder.pipeline_status?.id || ''}
                  onChange={(e) => handleStatusChange(selectedOrder.id, parseInt(e.target.value))}
                >
                  <option value="">-- Select Status --</option>
                  {pipelineStatuses.map(status => (
                    <option key={status.id} value={status.id}>{status.name}</option>
                  ))}
                </select>
              </FormGroup>
              
              <FormGroup>
                <label>Notes</label>
                <textarea value={selectedOrder.notes || ''} disabled />
              </FormGroup>
              
              {/* Items summary */}
              <div style={{ marginTop: '1rem' }}>
                <label style={{ fontWeight: 500, marginBottom: '0.5rem', display: 'block' }}>
                  Items ({selectedOrder.items_count})
                </label>
                <Button className="secondary" onClick={() => navigate(`/orders/${selectedOrder.id}/edit`)}>
                  View Full Details →
                </Button>
              </div>
            </ModalBody>
            
            <ModalFooter>
              <Button className="secondary" onClick={() => handlePreviewPdf(selectedOrder)}>
                👁️ Preview PDF
              </Button>
              <Button className="secondary" onClick={() => handleDownloadPdf(selectedOrder.id)}>
                📄 Download
              </Button>
              <Button className="secondary" onClick={() => handleGenerateContract(selectedOrder.id)}>
                📝 Contract
              </Button>
              <Button className="secondary" onClick={() => handleSendEmail(selectedOrder.id, 'confirmation', 'customer')}>
                ✉️ Email
              </Button>
              <Button className="secondary" onClick={() => handleDuplicateOrder(selectedOrder.id)}>
                📋 Duplicate
              </Button>
              <Button className="primary" onClick={() => navigate(`/orders/${selectedOrder.id}/edit`)}>
                Edit Order
              </Button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* PDF Preview Modal */}
      {pdfPreviewOrder && (
        <PdfPreviewModal
          orderId={pdfPreviewOrder.id}
          orderNumber={pdfPreviewOrder.order_number}
          onClose={() => setPdfPreviewOrder(null)}
          defaultTemplate={pdfPreviewOrder.order_type === 'configurator' ? 'quote' : 'invoice'}
        />
      )}
    </PageContainer>
  );
}

export default OrdersPipeline;
